/* =========================================================
   Field schema — metadata-driven forms.
   States: editable (amber) · readonly (green) · required (red)
           · blocked (slate, dimmed)

   This is the central principle of the UI: every field declares
   its state here, and the generic FieldRow renders and gates it
   accordingly. Colour is never the only signal — each state also
   carries an icon, a tooltip and an aria-label (AA).
   ========================================================= */
import type { Completeness, FieldDef, FieldSchema, FieldState } from '@/types';
import { ic, type IconElement } from '@/components/Icons';

export interface StateToken {
  color: string;
  bg: string;
  icon: IconElement;
  key: FieldState;
}

export const STATE: Record<FieldState, StateToken> = {
  editable: { color: 'var(--st-edit)', bg: 'var(--st-edit-l)', icon: ic.edit, key: 'editable' },
  readonly: { color: 'var(--st-read)', bg: 'var(--st-read-l)', icon: ic.eye,  key: 'readonly' },
  required: { color: 'var(--st-req)',  bg: 'var(--st-req-l)',  icon: ic.warn, key: 'required' },
  blocked:  { color: 'var(--st-lock)', bg: 'var(--st-lock-l)', icon: ic.lock, key: 'blocked'  },
};

/* ---------- Validation ---------- */

const RX = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?\d[\d\s().-]{6,}$/,
  postal: /^[A-Z0-9-]{3,10}$/i,
};

export type ValidationError =
  | 'required_empty' | 'postal_invalid' | 'email_invalid' | 'phone_invalid';

export function validateField(field: FieldDef, value: unknown): ValidationError | null {
  const v = (value ?? '').toString().trim();
  if (field.state === 'required' && !v) return 'required_empty';
  // "0000" is the legacy sentinel for a missing postal code — treated as empty
  if (field.kind === 'postal' && (v === '' || v === '0000')) {
    return field.state === 'required' ? 'postal_invalid' : null;
  }
  return formatError(field, v);
}

/** Validación para la importación del Excel maestro.
 *
 *  Comprueba el formato, no la completitud. Un campo rojo vacío NO es un
 *  error aquí: el maestro de ETC viene con huecos a propósito, y llenarlos
 *  durante la ventana de actualización es precisamente el trabajo que
 *  Connect le pide a cada partner. Rechazar esas filas al importar
 *  impediría cargar el maestro y dejaría el portal sin nada que completar.
 *
 *  Un correo mal escrito o un código postal imposible sí se rechazan: eso
 *  no es un hueco, es un dato equivocado, y entra igual de mal hoy que
 *  dentro de tres meses. */
export function validateImportField(field: FieldDef, value: unknown): ValidationError | null {
  const v = (value ?? '').toString().trim();
  // vacío, o el centinela heredado, pasan sin más
  if (!v || (field.kind === 'postal' && v === '0000')) return null;
  return formatError(field, v);
}

function formatError(field: FieldDef, v: string): ValidationError | null {
  if (field.kind === 'postal' && v && !RX.postal.test(v)) return 'postal_invalid';
  if (field.kind === 'email' && v && !RX.email.test(v)) return 'email_invalid';
  if (field.kind === 'phone' && v && !RX.phone.test(v)) return 'phone_invalid';
  return null;
}

/* ---------- Org schema ---------- */

export const orgSchema: FieldSchema = [
  // generales
  { key:'OrgId',                   label_es:'ID de organización',       label_en:'Organization ID',        section:'general', state:'readonly', kind:'mono' },
  { key:'OrgName',                 label_es:'Nombre',                   label_en:'Name',                   section:'general', state:'readonly' },
  { key:'OrgWebAddress',           label_es:'Sitio web',                label_en:'Website',                section:'general', state:'editable', kind:'url' },
  { key:'YearJoined',              label_es:'Año de ingreso',           label_en:'Year joined',            section:'general', state:'editable' },
  { key:'OrgCountry',              label_es:'País',                     label_en:'Country',                section:'general', state:'readonly', kind:'country' },
  { key:'RegisteredCity',          label_es:'Ciudad registrada',        label_en:'Registered city',        section:'general', state:'readonly' },
  // registered address
  { key:'RegisteredAddress1',      label_es:'Dirección 1',              label_en:'Address line 1',         section:'registered_address', state:'editable' },
  { key:'RegisteredAddress2',      label_es:'Dirección 2',              label_en:'Address line 2',         section:'registered_address', state:'editable' },
  { key:'RegisteredAddress3',      label_es:'Dirección 3',              label_en:'Address line 3',         section:'registered_address', state:'editable' },
  { key:'RegisteredStateProvince', label_es:'Estado / provincia',       label_en:'State / province',       section:'registered_address', state:'editable' },
  { key:'RegisteredPostalCode',    label_es:'Código postal',            label_en:'Postal code',            section:'registered_address', state:'editable', kind:'postal' },
  // contractual (green)
  { key:'ContractAddress1',        label_es:'Dirección 1',              label_en:'Address line 1',         section:'contract_address', state:'readonly' },
  { key:'ContractAddress2',        label_es:'Dirección 2',              label_en:'Address line 2',         section:'contract_address', state:'readonly' },
  { key:'ContractAddress3',        label_es:'Dirección 3',              label_en:'Address line 3',         section:'contract_address', state:'readonly' },
  { key:'ContractCity',            label_es:'Ciudad',                   label_en:'City',                   section:'contract_address', state:'readonly' },
  { key:'ContractStateProvince',   label_es:'Estado / provincia',       label_en:'State / province',       section:'contract_address', state:'readonly' },
  { key:'ContractCountry',         label_es:'País',                     label_en:'Country',                section:'contract_address', state:'readonly', kind:'country' },
  { key:'ContractPostalCode',      label_es:'Código postal',            label_en:'Postal code',            section:'contract_address', state:'readonly' },
  // billing address (yellow)
  { key:'InvoicingAddress1',       label_es:'Dirección 1',              label_en:'Address line 1',         section:'billing_address', state:'editable' },
  { key:'InvoicingAddress2',       label_es:'Dirección 2',              label_en:'Address line 2',         section:'billing_address', state:'editable' },
  { key:'InvoicingAddress3',       label_es:'Dirección 3',              label_en:'Address line 3',         section:'billing_address', state:'editable' },
  { key:'InvoicingCity',           label_es:'Ciudad',                   label_en:'City',                   section:'billing_address', state:'editable' },
  { key:'InvoicingStateProvince',  label_es:'Estado / provincia',       label_en:'State / province',       section:'billing_address', state:'editable' },
  { key:'InvoicingCountry',        label_es:'País',                     label_en:'Country',                section:'billing_address', state:'editable', kind:'country' },
  { key:'InvoicingPostalCode',     label_es:'Código postal',            label_en:'Postal code',            section:'billing_address', state:'editable', kind:'postal' },
  // billing contact (yellow)
  { key:'BillingContactFirstName', label_es:'Nombre',                   label_en:'First name',             section:'billing_contact', state:'editable' },
  { key:'BillingContactLastName',  label_es:'Apellido',                 label_en:'Last name',              section:'billing_contact', state:'editable' },
  { key:'BillingContactEmailAddress', label_es:'Email',                 label_en:'Email',                  section:'billing_contact', state:'editable', kind:'email' },
  { key:'BillingContactTelephone', label_es:'Teléfono',                 label_en:'Phone',                  section:'billing_contact', state:'editable', kind:'phone' },
  { key:'BillingContactFax',       label_es:'Fax',                      label_en:'Fax',                    section:'billing_contact', state:'editable' },
  // legal contact (green / readonly — read-only for every partner role)
  { key:'LegalContactFirstName',   label_es:'Nombre',                   label_en:'First name',             section:'legal_contact', state:'readonly' },
  { key:'LegalContactLastName',    label_es:'Apellido',                 label_en:'Last name',              section:'legal_contact', state:'readonly' },
  { key:'LegalContactEmailAddress',label_es:'Email',                    label_en:'Email',                  section:'legal_contact', state:'readonly', kind:'email' },
  { key:'LegalContactTelephone',   label_es:'Teléfono',                 label_en:'Phone',                  section:'legal_contact', state:'readonly', kind:'phone' },
  { key:'LegalContactFax',         label_es:'Fax',                      label_en:'Fax',                    section:'legal_contact', state:'readonly' },
];

/* ---------- Site schema ---------- */

export const siteSchema: FieldSchema = [
  // identifiers (blocked / black)
  { key:'OrgId',         label_es:'ID de organización',  label_en:'Organization ID', section:'identifiers', state:'blocked', kind:'mono' },
  { key:'OrgName',       label_es:'Organización',        label_en:'Organization',    section:'identifiers', state:'blocked' },
  { key:'OrgWebAddress', label_es:'Sitio web org.',      label_en:'Org website',     section:'identifiers', state:'blocked', kind:'url' },
  { key:'PrimarySiteId', label_es:'Site primario',       label_en:'Primary site ID', section:'identifiers', state:'blocked', kind:'mono' },
  { key:'SiteId',        label_es:'ID del site',         label_en:'Site ID',         section:'identifiers', state:'blocked', kind:'mono' },
  { key:'ATCCSN',        label_es:'ATCCSN',              label_en:'ATCCSN',          section:'identifiers', state:'readonly', kind:'mono' },
  { key:'SiteCountry',   label_es:'País',                label_en:'Country',         section:'identifiers', state:'readonly', kind:'country' },
  { key:'SiteName',      label_es:'Nombre del site',     label_en:'Site name',       section:'identifiers', state:'readonly' },

  // site data (yellow + reds)
  { key:'CommercialSiteName',    label_es:'Nombre comercial',      label_en:'Commercial site name', section:'site_data', state:'required' },
  { key:'Workstations',          label_es:'Estaciones de trabajo', label_en:'Workstations',         section:'site_data', state:'required', kind:'number' },
  { key:'SiteAddress1',          label_es:'Dirección 1',           label_en:'Address line 1',       section:'site_data', state:'editable' },
  { key:'SiteAddress2',          label_es:'Dirección 2',           label_en:'Address line 2',       section:'site_data', state:'editable' },
  { key:'SiteAddress3',          label_es:'Dirección 3',           label_en:'Address line 3',       section:'site_data', state:'editable' },
  { key:'SiteCity',              label_es:'Ciudad',                label_en:'City',                 section:'site_data', state:'editable' },
  { key:'SiteStateProvince',     label_es:'Estado / provincia',    label_en:'State / province',     section:'site_data', state:'editable' },
  { key:'SitePostalCode',        label_es:'Código postal',         label_en:'Postal code',          section:'site_data', state:'editable', kind:'postal' },
  { key:'RegisteredPostalCode',  label_es:'Código postal registrado', label_en:'Registered postal code', section:'site_data', state:'required', kind:'postal' },
  { key:'RegisteredStateProvince', label_es:'Estado / provincia registrada', label_en:'Registered state', section:'site_data', state:'editable' },
  { key:'SiteTelephone',         label_es:'Teléfono',              label_en:'Phone',                section:'site_data', state:'editable', kind:'phone' },
  { key:'SiteFax',               label_es:'Fax',                   label_en:'Fax',                  section:'site_data', state:'editable' },
  { key:'SiteEmailAddress',      label_es:'Email (Info)',          label_en:'Email (Info)',         section:'site_data', state:'editable', kind:'email' },
  { key:'SiteWebAddress',        label_es:'Sitio web',             label_en:'Web address',          section:'site_data', state:'editable', kind:'url' },

  // ATC Director
  { key:'ATCDirectorFirstName', label_es:'Nombre',   label_en:'First name', section:'atc_director', state:'editable' },
  { key:'ATCDirectorLastName',  label_es:'Apellido', label_en:'Last name',  section:'atc_director', state:'editable' },
  { key:'ATCDirectorEmail',     label_es:'Email',    label_en:'Email',      section:'atc_director', state:'editable', kind:'email' },
  { key:'ATCDirectorTelephone', label_es:'Teléfono', label_en:'Phone',      section:'atc_director', state:'editable', kind:'phone' },
  { key:'ATCDirectorFax',       label_es:'Fax',      label_en:'Fax',        section:'atc_director', state:'editable' },

  // Site Admin
  { key:'SiteAdminFirstName', label_es:'Nombre',   label_en:'First name', section:'site_admin', state:'editable' },
  { key:'SiteAdminLastName',  label_es:'Apellido', label_en:'Last name',  section:'site_admin', state:'editable' },
  { key:'SiteAdminEmail',     label_es:'Email',    label_en:'Email',      section:'site_admin', state:'editable', kind:'email' },
  { key:'SiteAdminTelephone', label_es:'Teléfono', label_en:'Phone',      section:'site_admin', state:'editable', kind:'phone' },
  { key:'SiteAdminFax',       label_es:'Fax',      label_en:'Fax',        section:'site_admin', state:'editable' },

  // Site Manager
  { key:'SiteManagerFirstName', label_es:'Nombre',   label_en:'First name', section:'site_manager', state:'editable' },
  { key:'SiteManagerLastName',  label_es:'Apellido', label_en:'Last name',  section:'site_manager', state:'editable' },
  { key:'SiteManagerEmail',     label_es:'Email',    label_en:'Email',      section:'site_manager', state:'editable', kind:'email' },
  { key:'SiteManagerTelephone', label_es:'Teléfono', label_en:'Phone',      section:'site_manager', state:'editable', kind:'phone' },
  { key:'SiteManagerFax',       label_es:'Fax',      label_en:'Fax',        section:'site_manager', state:'editable' },
];

/* ---------- Instructor schema ---------- */

export const instructorSchema: FieldSchema = [
  // personal (yellow)
  { key:'InstructorId',     label_es:'ID de instructor', label_en:'Instructor ID',      section:'identifiers', state:'readonly', kind:'mono' },
  { key:'EnglishFirstName', label_es:'Nombre',           label_en:'English first name', section:'personal', state:'editable' },
  { key:'EnglishLastName',  label_es:'Apellido',         label_en:'English last name',  section:'personal', state:'editable' },
  { key:'EmailAddress',     label_es:'Email',            label_en:'Email',              section:'personal', state:'editable', kind:'email' },
  { key:'Mobile1',          label_es:'Móvil',            label_en:'Mobile',             section:'personal', state:'editable', kind:'phone' },
  { key:'Telephone1',       label_es:'Teléfono',         label_en:'Phone',              section:'personal', state:'editable', kind:'phone' },
  // reference (green)
  { key:'JobTitle',      label_es:'Cargo',           label_en:'Job title',     section:'identifiers', state:'readonly' },
  { key:'Country',       label_es:'País',            label_en:'Country',       section:'identifiers', state:'readonly', kind:'country' },
  { key:'Certification', label_es:'Certificación',   label_en:'Certification', section:'identifiers', state:'readonly' },
  { key:'AccessRoles',   label_es:'Roles de acceso', label_en:'Access roles',  section:'identifiers', state:'readonly' },
];

/** La afiliación de un instructor a un site.
 *
 *  No es una ficha que se edite campo a campo como las otras tres: es un
 *  enlace entre dos cosas. Existe como esquema porque la importación lo
 *  necesita —para construir la plantilla, para emparejar las columnas del
 *  archivo y para validar antes de subir—, no porque haya una pantalla
 *  que lo pinte.
 *
 *  Un instructor puede estar en varios sites a la vez, y su cargo puede
 *  ser distinto en cada uno. Por eso son filas, y no una columna con
 *  comas dentro de la plantilla de instructores: una columna así no puede
 *  guardar un cargo por site. */
export const assignmentSchema: FieldSchema = [
  { key:'InstructorId',  label_es:'ID de instructor', label_en:'Instructor ID', section:'identifiers', state:'editable', kind:'mono' },
  { key:'SiteId',        label_es:'ID del site',      label_en:'Site ID',       section:'identifiers', state:'editable', kind:'mono' },
  { key:'PrimaryRoleId', label_es:'Cargo en el site', label_en:'Role at site',  section:'identifiers', state:'editable' },
  { key:'Status',        label_es:'Estado',           label_en:'Status',        section:'identifiers', state:'editable' },
];

export const schemas = { orgSchema, siteSchema, instructorSchema, assignmentSchema };

/* ---------- Helpers ---------- */

export function fieldsBySection(schema: FieldSchema, section: string): FieldSchema {
  return schema.filter((f) => f.section === section);
}

export function requiredFields(schema: FieldSchema): FieldSchema {
  return schema.filter((f) => f.state === 'required');
}

/**
 * Progress for one record.
 *
 * Denominator: only fields declared `state: 'required'` — the red ones.
 * Numerator: those that hold a value which is neither blank after trim
 * nor the legacy '0000' postal sentinel.
 */
export function completeness(
  schema: FieldSchema,
  record: Record<string, unknown> | null | undefined,
): Completeness {
  const reqs = requiredFields(schema);
  if (!reqs.length) return { pct: 100, missing: 0, total: 0 };
  const filled = reqs.filter(
    (f) => (record?.[f.key] ?? '').toString().trim() && record?.[f.key] !== '0000',
  ).length;
  return {
    pct: Math.round((filled / reqs.length) * 100),
    missing: reqs.length - filled,
    total: reqs.length,
  };
}

/** Every red field that still blocks saving on this record. */
export function missingRequired(
  schema: FieldSchema,
  record: Record<string, unknown> | null | undefined,
): FieldSchema {
  return requiredFields(schema).filter(
    (f) => !(record?.[f.key] ?? '').toString().trim() || record?.[f.key] === '0000',
  );
}

/** True when nothing blocks a save (no empty reds, no invalid values). */
export function canSave(
  schema: FieldSchema,
  record: Record<string, unknown> | null | undefined,
): boolean {
  return !schema.some((f) => {
    if (f.state === 'readonly' || f.state === 'blocked') return false;
    return validateField(f, record?.[f.key]) !== null;
  });
}
